import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { useTranscriberDeleteControllerDeleteTranscriberMutation } from '@shared/services/apiService';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { useTranscriberContext } from '../TranscriberContext';

export const TranscriberDeleteDialog: React.FC = () => {
  const { dialogState, transcriber, setTranscriber, closeDialog, refreshTranscribers } =
    useTranscriberContext();

  const [deleteTranscriber] = useTranscriberDeleteControllerDeleteTranscriberMutation();

  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');

  const handleErrorResponse = (error: unknown) => {
    const e: any = error;
    const errorMessage =
      e && e.data && typeof e.data === 'object' && 'message' in e.data
        ? e.data.message
        : e?.message || t('superAdminManageVoice.genericError');
    toast.error((props) => <CustomErrorToast {...props} message={errorMessage} />, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  };

  const handleSuccessResponse = (message: string) => {
    toast.success(message, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
    setTranscriber(undefined);
    closeDialog('createOpen');
    refreshTranscribers();
  };

  const handleDeleteConfirmed = async () => {
    if (!transcriber) return;
    try {
      await deleteTranscriber({ id: transcriber.id }).unwrap();
      handleSuccessResponse(t('transcriberPage.deleteSuccess'));
    } catch (err: unknown) {
      handleErrorResponse(err);
    }
  };

  const handleClose = () => {
    setTranscriber(undefined);
    closeDialog('deleteOpen');
  };

  return (
    <Dialog open={dialogState.deleteOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('transcriberPage.confirmDeleteTitle')}</DialogTitle>
      <DialogContent>
        <Typography>{t('transcriberPage.deleteInstruction')}</Typography>
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: 'Monaco, Menlo, monospace' }}>
            {transcriber
              ? t('transcriberPage.deleteConfirmationPhrase', {
                  provider: transcriber.provider,
                  language: transcriber.language,
                })
              : ''}
          </Typography>
        </Box>
        <TextField
          margin="dense"
          label={t('transcriberPage.typeConfirmationLabel')}
          fullWidth
          value={deleteConfirmationInput}
          onChange={(e) => setDeleteConfirmationInput(e.target.value)}
          onPaste={(e) => {
            // Disable pasting into the confirmation field to force manual typing
            e.preventDefault();
          }}
          onDrop={(e) => {
            // Disable dropping text into the field
            e.preventDefault();
          }}
          onKeyDown={(e) => {
            // Prevent Cmd/Ctrl+V paste via keyboard shortcuts
            if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
              e.preventDefault();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button
          color="error"
          onClick={handleDeleteConfirmed}
          disabled={
            !(
              transcriber &&
              deleteConfirmationInput.trim() ===
                t('transcriberPage.deleteConfirmationPhrase', {
                  provider: transcriber.provider,
                  language: transcriber.language,
                })
            )
          }>
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
